import React, {Suspense, useEffect, lazy, useState} from 'react';
import {useTranslation} from "react-i18next";
import Joyride, {ACTIONS, EVENTS} from 'react-joyride';
import {Helmet} from "react-helmet";
import {useRecoilState, useRecoilValue} from "recoil";
import {BrowserRouter as Router, Switch, Route, Redirect} from "react-router-dom";
import {ConfigProvider, Row, Col} from 'antd';
import i18n from "i18next";

// Components
import PageLoading from "sharedComponents/Loading/PageLoading";
import Login from '../Login/Login';
import ExternalSurveys from "./components/components/ExternalSurveys";
import ExternalSurveysSuccess from "./components/components/ExternalSurveysSuccess";

// HOC
import NotFoundUI from "HOC/withPermissionErrorBoundary/NotFoundUI";

// Store
import {
    selectCompanyData,
    selectGeneralCurrentSettings,
    selectIsUserFirstLoggedIn,
    selectMe,
    selectRole,
    selectSidebarCollapsed
} from "store/selectors";
import {isFreeTrial, isQuickTourActive, isYourCompanyBlocked} from "store";

// Hooks
import {useTimezone, usePersist, useTaskManager, dispatch} from 'Hooks';
import {useAuth, useDeviceType} from "context";

// Styles
import './style/dashboard.scss';

// Utils
import {getPermission} from "../../../permissions/permissions.config";
import getQuickTourSteps from "utils/getQuickTourSteps";
import {getGamificationRoutes} from "utils/memoize";

// Models
import {auth} from "models/auth";
import { generalSetting } from "models/generalSetting";
import {user as profileUser} from "models/user";
import {deel} from "models/deel";

// Assets
import eleapLogoDark from '../../../assets/images/eleap-logo-dark.svg';

// Config
import {initializeMomentLocale, getAntdLocale} from "../../../localeConfig"

// Lazy Components
const Notifications = React.lazy(() => import("sharedComponents/Notifications"));
const SideMenu = lazy(() => import('sharedComponents/SideMenu/SideMenu'));
const UserActions = lazy(() => import("sharedComponents/UserActions/UserActions"));
const Footer = lazy(() => import("sharedComponents/Footer/Footer.jsx"));
const ExportIndicator = lazy(() => import("sharedComponents/ExportIndicator"));
const SelectionBar = lazy(() => import("sharedComponents/SelectionBar"));
const PulsePointPopUp = lazy(() => import("sharedComponents/PulsePointPopUp"));
const CompanySettings = lazy(() => import('pages/CompanySettings/CompanySettings.js'));
const FreeTrial =  lazy(() => import('./FreeTrial'));
const BlockedAccount = lazy(() => import("./BlockedAccount"));

const Dashboard = () => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [antdLocale, setAntdLocale] = useState(null)
    const {authTokens} = useAuth();
    const {isMobile} = useDeviceType();
    const {t} = useTranslation()
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

    const pathSegments = window.location.pathname.split('/');
    const segment1 = pathSegments[2];
    const segment2 = pathSegments[3];

    const isExternalSurveyPage = pathSegments[1] === 'es-pms';
    const isExternalSurveySuccessPage = isExternalSurveyPage && segment1 === 'success' && segment2;


    // Deel
    const url = new URL(window.location.href);
    const hostParts = window.location.hostname.split(".");
    const subdomain = hostParts[0]; // "deel"

    const code = url.searchParams.get("code");
    const companyDomain = url.searchParams.get("state");

    useEffect(() => {
        const handleDeelRedirect = async () => {
            const redirectUrl = `${window.location.protocol}//${companyDomain}.${process.env.REACT_APP_DOMAIN_NAME}/settings?tab=integrations&activePanel=Deel`;
            let success = false;

            if (subdomain !== "deel") {
                return;
            }

            if (!code || !companyDomain) {
                window.location.href = redirectUrl + "&success=0";
                return;
            }

            try {
                const response = await deel.postDeelCode(code, companyDomain);

                if (response?.data?.status) {
                    success = true;
                }
            } catch (error) {
                console.error("Deel integration failed:", error);
            } finally {
                window.location.href = redirectUrl + (success ? "&success=1" : "&success=0");
            }
        };

        handleDeelRedirect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useTimezone();
    useTaskManager([
        {
            cronDate: '03:00:00',
            fn: () => {
                dispatch({type: 'OPEN_POP_UP'});
            }
        }]);
    const isSidebarCollapsed = useRecoilValue(selectSidebarCollapsed);
    const [isTourActive, setIsTourActive] = useRecoilState(isQuickTourActive);
    const userRole = useRecoilValue(selectRole);
    const user = useRecoilValue(selectMe);
    const companyWholeData = useRecoilValue(selectCompanyData);
    const isFirstLogin = useRecoilValue(selectIsUserFirstLoggedIn);
    const generalSettings = useRecoilValue(selectGeneralCurrentSettings);
    usePersist('user', user);
    const routesWithGamification = getGamificationRoutes(generalSettings, user);
    const [isFreeTrialExpired, SetIsFreeTrialExpired] = useRecoilState(isFreeTrial);
    const [isAccountBlocked, setIsAccountBlocked] = useRecoilState(isYourCompanyBlocked);
    const [freeTrialAdmin, SetFreeTrialAdmin] = useState(null);

    const handleTourActions = (event) => {
        if (event.action === ACTIONS.CLOSE || event.type === EVENTS.TOUR_END) {
            setIsTourActive(false);
        }
    };

    const checkFreeTrial = async () => {
        try {
            const response = await auth.checkFreeTrial();

            if (!response?.data?.success) {
                throw new Error(response?.message);
            }

            SetIsFreeTrialExpired(response?.data?.free_trial_expires);
            SetFreeTrialAdmin(response?.data?.user);
        } catch (e) {
            console.log(e);
        }
    };

    const checkUnpaidInvoices = async () => {
        try {
            const response = await auth.checkUnpaidInvoices();

            if (!response?.data?.success) {
                throw new Error(response?.message);
            }

            setIsAccountBlocked(response?.data?.unpaid_invoice);
        } catch (e) {
            console.log(e);
        }
    };

    const initAppLocalization = async () => {
            try {
                const { data: companyData } = await generalSetting.getGeneralSetting();
                const { data: userData } = await profileUser.user();

                if (companyData?.success) {
                    const companyLang = companyData.general_setting.language?.code;
                    const userLang = userData.user.language?.code;

                    const finalLang = userLang || companyLang || "en";
                    const savedLang = localStorage.getItem("language");

                    if (finalLang && savedLang !== finalLang) {
                        localStorage.setItem("language", finalLang);
                    }

                    if (i18n.language !== finalLang) {
                        await i18n.changeLanguage(finalLang);
                    }

                    await initializeMomentLocale(finalLang)
                    const locale = await getAntdLocale(finalLang)
                    setAntdLocale(locale)
                }
            } catch (err) {
                console.warn("Language or settings init failed:", err);
            } finally {
                setIsInitialized(true);
            }
        };

    useEffect(() => {
        if (!authTokens) {
            return;
        }

        checkUnpaidInvoices();
        checkFreeTrial();
        initAppLocalization();

        const urlParams = new URLSearchParams(window.location.search);
        const token = urlParams.get('redeapp_jwt');

        if (token) {
            window.location.href = `${process.env.REACT_APP_URL_PREFIX}/dashboard`;
        }
    }, [authTokens]);

    useEffect(() => {
        const handleLanguageChange = async (lng) => {
            await initializeMomentLocale(lng);
            const locale = await getAntdLocale(lng);
            setAntdLocale(locale);
        };

        i18n.on('languageChanged', handleLanguageChange);
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, []);

    return (
        <ConfigProvider locale={antdLocale}>
            <Router>
            <Helmet>
                <title>
                    {user?.company?.name || companyWholeData?.name || 'PMS'}
                </title>
            </Helmet>
            {authTokens ? (
                <Row className="dashboard">
                    <Suspense>
                        <SideMenu/>
                    </Suspense>
                    <Col className={`app_content ${isSidebarCollapsed ? 'sidebar_collapsed' : ''}`}>
                        <Row>
                            <Suspense>
                                <UserActions/>
                            </Suspense>

                            {isAccountBlocked ? (
                                <Switch>
                                    {/* Route for '/settings' */}
                                    {userRole.roleId === 1 &&
                                        <Route path='/settings'>
                                            <Suspense fallback={<PageLoading/>}>
                                                <CompanySettings expired={isAccountBlocked} />
                                            </Suspense>
                                        </Route>

                                    }

                                    {/* Route for other pages */}
                                    <Suspense fallback={<PageLoading/>}>
                                        <BlockedAccount
                                            userRole={userRole}
                                            freeTrialAdmin={freeTrialAdmin}
                                        />
                                    </Suspense>
                                </Switch>
                            ) : !isFreeTrialExpired ? (
                                <Switch>
                                    <Redirect
                                        exec
                                        from={`${process.env.REACT_APP_URL_PREFIX}/login`}
                                        to={`${process.env.REACT_APP_URL_PREFIX}/dashboard`}
                                    />
                                    {generalSettings?.id ? userRole.roleId && routesWithGamification.map(({
                                             path,
                                             Component,
                                             type,
                                             isAvailable
                                         }) => isAvailable ? (
                                        getPermission(userRole, type) ? (
                                            <Route key={path} exact path={process.env.REACT_APP_URL_PREFIX + path}>
                                                <Suspense fallback={<PageLoading/>}>
                                                    <Component/>
                                                </Suspense>
                                            </Route>
                                        ) : (
                                            <Redirect
                                                key={path}
                                                from={process.env.REACT_APP_URL_PREFIX + path}
                                                to={`${process.env.REACT_APP_URL_PREFIX}/dashboard`}
                                            />
                                        )
                                    ) : null)
                                    : <PageLoading/>
                                }

                                {userRole.roleId && (
                                    <Route path="/*">
                                        <NotFoundUI/>
                                    </Route>
                                )}
                                </Switch>
                            ) : (
                                <Switch>
                                    {/* Route for '/settings' */}
                                    {userRole.roleId === 1 &&
                                        <Route path='/settings'>
                                            <Suspense fallback={<PageLoading/>}>
                                                <CompanySettings expired={isFreeTrialExpired} />
                                            </Suspense>
                                        </Route>

                                    }

                                    {/* Route for other pages */}
                                    <Suspense fallback={<PageLoading/>}>
                                        <FreeTrial
                                            userRole={userRole}
                                            freeTrialAdmin={freeTrialAdmin}
                                        />
                                    </Suspense>
                                </Switch>)
                            }
                        </Row>

                        <Suspense>
                            <ExportIndicator/>

                            <SelectionBar/>

                            <Footer footerLogo={eleapLogoDark}/>
                        </Suspense>
                    </Col>
                    <Suspense>
                        {(isFreeTrialExpired === false) && (isAccountBlocked === false) &&
                            <Notifications/>
                        }

                        {!!generalSettings?.pulse_point && !user?.cms_user && !isFreeTrialExpired && !isAccountBlocked &&
                            <div className="pulse_point_section">
                                <PulsePointPopUp/>
                            </div>
                        }

                        {(!!isFirstLogin || !!isTourActive) && !isMobile &&
                            <Joyride
                                callback={handleTourActions}
                                steps={getQuickTourSteps(userRole, t)}
                                continuous={true}
                                scrollOffset={80}
                                showProgress={true}
                                showSkipButton={true}
                                disableCloseOnEsc={true}
                                disableOverlayClose={true}
                                locale={{
                                    back: t("Back"),
                                    close: t("Close"),
                                    last: t("Last"),
                                    next: t("Next"),
                                    skip: t("Skip"),
                                }}
                                styles={{
                                    options: {
                                        primaryColor: '#3932E0',
                                        zIndex: 2147483002, // this because of the "intercom-frame" had the biggest z-index, so we had to exceed it
                                    }
                                }}
                            />
                        }
                    </Suspense>
                </Row>) : (
                isExternalSurveyPage && uuidRegex.test(segment1) ? (
                    <ExternalSurveys UUID={segment1} />
                ) : isExternalSurveySuccessPage ? (
                    <ExternalSurveysSuccess surveyId={segment2} />
                ) : subdomain === "deel" && code && companyDomain ? (
                    <PageLoading />
                ) : (
                    <Login />
                )
            )}
        </Router>
        </ConfigProvider>
    );
};

export default React.memo(Dashboard);
